import { persistGeneratedBlocks, saveProjectMeta } from '@/services/project-api'
import { useStudioStore } from '@/store/studio-store'
import { postNativeUiBlockGenerate } from './native-ui-block-generation-client'

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

export type NativeUiBlockStudioGenerationResult =
  | { kind: 'generated'; projectId: string }
  | { kind: 'unavailable' }
  | { kind: 'failed'; message: string }

export function extractGeneratedBlocks(
  cogoJson: unknown,
): Record<string, unknown> {
  return readRecord(readRecord(cogoJson).blocks)
}

export async function applyNativeUiBlockGeneratedApp(
  cogoJson: unknown,
): Promise<string> {
  const blocks = extractGeneratedBlocks(cogoJson)
  if (Object.keys(blocks).length === 0) {
    throw new Error('native-ui-block-generation-no-blocks')
  }
  const root = readRecord(cogoJson)
  const manifest = readRecord(root.manifest)
  const name =
    typeof manifest.name === 'string' && manifest.name.trim()
      ? manifest.name.trim()
      : 'auth.register'
  const store = useStudioStore.getState()
  const projectId = await store.createServerProject(name)
  await persistGeneratedBlocks(projectId, blocks)
  const boundManifest =
    Object.keys(manifest).length > 0
      ? manifest
      : {
          name,
          pages: [
            {
              id: 'home',
              name: 'Home',
              route: '/',
              blocks: Object.keys(blocks),
            },
          ],
        }
  await saveProjectMeta(projectId, { manifest: boundManifest })
  await store.openServerProject(projectId, {
    navigate: true,
    activateWorkspace: true,
  })
  return projectId
}

function failureMessage(body: Record<string, unknown>, fallback: string): string {
  if (typeof body.status === 'string' && body.status) return body.status
  if (typeof body.error === 'string' && body.error) return body.error
  if (typeof body.reason === 'string' && body.reason) return body.reason
  return fallback
}

export async function tryNativeUiBlockStudioGeneration(
  prompt: string,
): Promise<NativeUiBlockStudioGenerationResult> {
  let response: Awaited<ReturnType<typeof postNativeUiBlockGenerate>>
  try {
    response = await postNativeUiBlockGenerate(prompt)
  } catch {
    return { kind: 'unavailable' }
  }

  const { httpStatus, body } = response
  // 503: documented lane-off (shared-key unset). 404: this host has no
  // Pages Function for the route (appliance nginx). Both are "not this
  // generator" — the caller falls through to app generation.
  if (httpStatus === 503 || httpStatus === 404) {
    return { kind: 'unavailable' }
  }
  if (httpStatus === 401) {
    return { kind: 'failed', message: failureMessage(body, 'unauthorized') }
  }
  if (httpStatus === 200 && body.status === 'no-match') {
    return { kind: 'unavailable' }
  }
  if (httpStatus === 200 && body.status === 'generated') {
    const value = readRecord(body.value)
    try {
      const projectId = await applyNativeUiBlockGeneratedApp(value.cogoJson)
      return { kind: 'generated', projectId }
    } catch (error) {
      return {
        kind: 'failed',
        message:
          error instanceof Error
            ? error.message
            : 'native-ui-block-generation-apply-failed',
      }
    }
  }
  return {
    kind: 'failed',
    message: failureMessage(body, 'native-ui-block-generation-failed'),
  }
}
